import { CustomMarginType } from "../../types/types";
import { Logger } from "../../../../lib/logger/Logger";
import { postalCodeToCanton } from "../../lib/constants";
import { cleanUmlaute } from "../../hooks/useHandleExportDocument/export-document-utils";
import { getPatientDisplayName } from "@/lib/utils/client-utils";

export function findEarliestAndLatest(
	docs: any[],
	key: string
): { earliest: string | null; latest: string | null } {
	if (docs.length === 0) {
		return { earliest: null, latest: null };
	}

	const { earliest, latest } = docs.reduce(
		(acc, doc) => {
			const currentDate = new Date(doc[key] || "").getTime();

			if (currentDate < new Date(acc.earliest || "").getTime()) {
				acc.earliest = doc[key];
			}
			if (currentDate > new Date(acc.latest || "").getTime()) {
				acc.latest = doc[key];
			}

			return acc;
		},
		{ earliest: docs[0][key], latest: docs[0][key] }
	);

	return { earliest: earliest, latest: latest };
}

export const getUnderscoredString = (str: string): string => {
	const trimmed = str.trim();
	return trimmed.includes(" ") ? trimmed.replace(/\s+/g, "_") : trimmed;
};

export function dateOlderThanSeconds(date: Date, seconds: number) {
	return date < new Date(Date.now() - seconds * 1000);
}

export function blobToBase64(blob: Blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			resolve(reader.result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

/**
 * Converts a base64 string to a Blob
 * @param base64String - Base64 encoded string (with or without data URI prefix)
 * @param mimeType - MIME type for the blob (default: 'application/octet-stream')
 * @param chunkSize - Size of chunks for processing large files (default: 512)
 * @returns Blob object
 */
export function base64ToBlob(
	base64String: string,
	mimeType: string = "application/octet-stream",
	chunkSize: number = 512
): Blob {
	// Remove data URI prefix if present (e.g., "data:application/pdf;base64,")
	const cleanBase64 = base64String.includes(",")
		? base64String.split(",")[1]
		: base64String;

	const binaryString = atob(cleanBase64);

	// For smaller files, use simple conversion
	if (binaryString.length <= chunkSize) {
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return new Blob([bytes], { type: mimeType });
	}

	// For larger files, use chunked processing to avoid memory issues
	const byteArrays = [];
	for (let offset = 0; offset < binaryString.length; offset += chunkSize) {
		const slice = binaryString.slice(offset, offset + chunkSize);
		const byteNumbers = new Array(slice.length);

		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	return new Blob(byteArrays, { type: mimeType });
}

// Convert Timestamp to Date
export function ConvertTimestampToDate(timestamp: string): string {
	const date = new Date(timestamp);
	return date.toLocaleDateString();
}

export const sanitizeFileName = (input: string): string => {
	const withoutUmlaute = cleanUmlaute(input);
	// Normalize the string to decompose accented characters (e.g., "é" becomes "e" + accent)
	// Then remove the accents after normalization
	const normalized = withoutUmlaute
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");
	// Remove any character not in the allowed set
	return normalized.replace(/[^A-Za-z0-9_/!.*()' &$@=;:+,?-]/g, "");
};

/**
 * goBackOrForwardDays - Returns a date in ISO format (YYYY-MM-DDT00:00:00.000Z) for the given number of days in the past or future
 * @param days - number of days to go back or forward
 * @returns ISO date
 */
export function goBackOrForwardDays(days: number) {
	const date = new Date();
	date.setDate(date.getDate() + days);
	const utcDate = new Date(
		Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
	);
	const isoDate = utcDate.toISOString().split("T")[0] + "T00:00:00.000Z";
	return isoDate;
}

/**
 *
 * @param input - string in format "2023-09-07T20:15:41.913034+00:00" (supabase timestampz)
 */
export function getDayAndTime(input: string | null): string {
	if (!input) {
		return "";
	}

	// Create a new date object from the input
	const date = new Date(input);

	// Create a new date object for the current date
	const currentDate = new Date();

	// Calculate the difference in milliseconds between the two dates
	const timeDifference = currentDate.getTime() - date.getTime();

	// Convert the difference to hours
	const hoursDifference = timeDifference / (1000 * 60 * 60);

	// Get the day of the week in Zurich's time zone
	const day = new Intl.DateTimeFormat("de-DE", {
		weekday: "long",
		timeZone: "Europe/Zurich",
	}).format(date);

	// If the date is more than 7 days in the past, return the date
	if (hoursDifference > 7 * 24) {
		if (hoursDifference > 30 * 24) {
			return `${day} ${new Intl.DateTimeFormat("de-DE", {
				dateStyle: "short",
				timeZone: "Europe/Zurich",
			}).format(date)}`;
		} else {
			// vor X Tagen
			return `vor ${Math.floor(hoursDifference / 24)} Tagen`;
		}
	}

	// Get the time without seconds in Zurich's time zone
	const hours = new Intl.DateTimeFormat("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: "Europe/Zurich",
	}).format(date);

	const formattedTime = hours;

	if (hoursDifference < 24 && !isYesterday(date)) {
		return `${formattedTime}`;
	}

	if (isYesterday(date)) {
		return `Gestern ${formattedTime}`;
	}

	return `${day} ${formattedTime}`;
}

/**
 * Checks if a given date is equal to "yesterday"
 *
 * @param inputDate The date you want to check
 */
function isYesterday(inputDate: Date): boolean {
	// Get the current date
	const currentDate = new Date();

	// Subtract one day to get yesterday's date
	currentDate.setDate(currentDate.getDate() - 1);

	// Compare the year, month, and date
	return (
		inputDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
		inputDate.getUTCMonth() === currentDate.getUTCMonth() &&
		inputDate.getUTCDate() === currentDate.getUTCDate()
	);
}

/**
 * formatDate - Formats a date string.
 * "short" (default): "(D)D.(M)M.YYYY" (e.g. 7.7.2026)
 * "long": written-out month (e.g. 7. Juli 2026)
 */
export function formatDate(
	input: string | null | undefined,
	format: "short" | "long" = "short"
): string {
	if (!input) {
		return "";
	}
	const date = new Date(input);
	if (format === "long") {
		return date.toLocaleDateString("de-CH", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	}
	return date.toLocaleDateString("de-CH");
}

/**
 * getTrueKeys - Returns an array of keys where the value is true
 * @param input - Object with keys and boolean values
 * @returns Array of keys where the value is true
 * @example const trueKeys = getTrueKeys({ key1: true, key2: false, key3: true }); // ["key1", "key3"]
 */
export const getTrueKeys = (input: { [key: string]: boolean }): string[] => {
	return Object.entries(input)
		.filter(([key, value]) => value) // Filters out entries where the value is not true
		.map(([key]) => key); // Returns only the keys
};

export const _getJobDocumentFilePath = (
	organizationId: string,
	clientId: number,
	jobId: number,
	jobDocumentId: number
) => {
	const path = `${organizationId}/${clientId}/${jobId}/${jobDocumentId}`;
	const bucketName = "job_files";
	return { path: path, bucketName: bucketName };
};

export const _getJobFilePath = (
	organizationId: string,
	clientId: number,
	jobId: number
) => {
	const path = `${organizationId}/${clientId}/${jobId}`;
	const bucketName = "job_document_files";
	return { path: path, bucketName: bucketName };
};

/**
 * Clean file name by replacing unsafe characters and consecutive whitespaces with underscore
 * @param filename The original file name
 * @returns Cleaned file name
 */
export function cleanFileName(filename: string): string {
	// Define the unsafe characters
	// eslint-disable-next-line
	const unsafeCharacters = /[\/\\:*?"<>|]/g;

	// Replace unsafe characters with an underscore
	let cleanedFilename = filename.replace(unsafeCharacters, "_");

	// Replace one or more consecutive whitespaces with a single underscore
	cleanedFilename = cleanedFilename.replace(/\s{2,}/g, "_");

	// Replace any remaining single whitespace characters with an underscore
	cleanedFilename = cleanedFilename.replace(/\s/g, "_");

	return cleanedFilename;
}

/**
 * getPdfMultiplier - Use IsPreviewContext to receive isPreview value, then use getPdfMultiplier to get the multiplier
 * @param {boolean} isPreview
 * @returns {number} multiplier - 0.75 for PDF export, 0.7 for preview
 */
export const getPdfMultiplier = (isPreview: boolean): number => {
	// For some reason what is 210mm for A4 comes out as 157.5mm when exporting, thus we apply a factor of 0.75
	// As an alternative, we could also apply scale 0.75 to the PDFExport component
	return isPreview ? 0.7 : 0.75;
};

/**
 * convertCustomMarginTypeToRem
 * @param {CustomMarginType} margin
 * @returns margin in rem
 * @example customMargin = convertCustomMarginTypeToRem("dense"); // "0.5rem"
 */
export const convertCustomMarginTypeToRem = (margin: CustomMarginType) => {
	return margin === "none"
		? "0rem"
		: margin === "dense"
			? "0.5rem"
			: "1.5rem";
};

/**
 * Helper function to create the secure patient name
 * @param patientId
 * @param firstName
 * @param lastName
 * @param isPrivacyOn
 */
export const createSecurePatientName = (
	patientId: string | null,
	firstName: string | null,
	lastName: string | null,
	isPrivacyOn: boolean,
	orderLastNameFirst: boolean = false
) => {
	if (isPrivacyOn) {
		const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
		const lastInitial = lastName?.charAt(0)?.toUpperCase() || "";
		if (firstInitial && lastInitial) {
			return orderLastNameFirst
				? `${lastInitial}. ${firstInitial}.`
				: `${firstInitial}. ${lastInitial}.`;
		}
		return firstInitial
			? `${firstInitial}.`
			: lastInitial
				? `${lastInitial}.`
				: "";
	} else if (patientId && !firstName && !lastName) {
		return "Unbenannter Patient";
	} else if (!patientId && !firstName && !lastName) {
		return "...";
	} else {
		return getPatientDisplayName(
			{ first_name: firstName, last_name: lastName },
			orderLastNameFirst
		);
	}
};

export const getCantonFromPostalCode = (postalCode: number): string => {
	return postalCodeToCanton[postalCode] ?? "";
};

/**
 * Takes a function that it repeats every interval until it returns success or the timeout is reached
 * The result from the passed function will be returned once it returns success
 */
export const repeatFunctionEveryIntervalUntilTimeout = async ({
	interval = 1000,
	timeout = 10000,
	functionToCall,
	resolveOnSuccessAndData = false,
}: {
	interval?: number;
	timeout?: number;
	functionToCall: () => Promise<{
		success: boolean;
		data?: unknown;
		error?: unknown;
	}>;
	resolveOnSuccessAndData?: boolean;
}): Promise<{
	success: boolean;
	data?: unknown;
	error?: unknown;
}> => {
	const startTime = Date.now();
	while (Date.now() - startTime < timeout) {
		try {
			Logger.info("Polling...");
			const response = await functionToCall();
			if (
				response.success &&
				(resolveOnSuccessAndData === false || response.data)
			) {
				return response;
			}
		} catch (error) {
			Logger.error(error, {}, "Polling error");
			return {
				success: false,
				error: error,
			};
		}
		await new Promise((resolve) => setTimeout(resolve, interval));
	}
	return {
		success: false,
		error: "Timeout",
	};
};

export function removeStringTag(xml: string): string {
	return xml
		.replace(
			/<string[^>]*>([\s\S]*?)<\/string>/g,
			(match, innerContent) => {
				return innerContent
					.replace(/&lt;/g, "<")
					.replace(/&gt;/g, ">")
					.replace(/&amp;/g, "&")
					.trim();
			}
		)
		.trim();
}

/**
 * Compares two objects and returns an object containing the properties that changed
 * @param oldObj The original object
 * @param newObj The new object
 * @param properties Optional array of specific properties to check
 * @returns An object mapping changed property names to {from, to} values
 */
export function getChangedProperties(
	oldObj: any,
	newObj: any,
	properties?: string[]
) {
	const changes: Record<string, { from: any; to: any }> = {};

	if (!oldObj || !newObj) return changes;

	const keysToCheck = properties || Object.keys(newObj);

	keysToCheck.forEach((key) => {
		if (!Object.hasOwn(oldObj, key) || !Object.hasOwn(newObj, key)) return;

		if (
			typeof oldObj[key] === "object" &&
			oldObj[key] !== null &&
			typeof newObj[key] === "object" &&
			newObj[key] !== null
		) {
			if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
				changes[key] = {
					from: oldObj[key],
					to: newObj[key],
				};
			}
		} else if (oldObj[key] !== newObj[key]) {
			changes[key] = {
				from: oldObj[key],
				to: newObj[key],
			};
		}
	});

	return changes;
}

/**
 * Formats a number as currency with Swiss locale formatting.
 * Uses apostrophe (') for thousands separator and dot (.) for decimal separator.
 * Example: 80'205.89 CHF
 */
export const formatCurrency = (
	value: number | null | undefined,
	currencyText: string | null | undefined = "CHF"
): string => {
	if (value === null || value === undefined || isNaN(value)) {
		return "";
	}

	// Use user's currency preference directly, trim whitespace, fallback to CHF if empty
	const currency = currencyText?.trim() || "CHF";

	// Round to 2 decimal places
	const rounded = Math.round(value * 100) / 100;

	// Split into integer and decimal parts
	const parts = Math.abs(rounded).toFixed(2).split(".");
	const integerPart = parts[0];
	const decimalPart = parts[1];

	// Add thousands separators (apostrophes)
	let formattedInteger = "";
	for (let i = integerPart.length - 1, count = 0; i >= 0; i--, count++) {
		if (count > 0 && count % 3 === 0) {
			formattedInteger = "'" + formattedInteger;
		}
		formattedInteger = integerPart[i] + formattedInteger;
	}

	// Build the formatted number string (dot for decimal separator)
	const sign = rounded < 0 ? "-" : "";
	const formattedNumber = `${sign}${formattedInteger}.${decimalPart}`;

	// Use the currency text directly as provided by the user
	return `${formattedNumber} ${currency}`;
};

/**
 * Formats a number as currency with Swiss locale formatting, returning "-" for null/undefined values.
 * This variant is useful for table displays where "-" is preferred over empty string.
 */
export const formatCurrencyWithDash = (
	value: number | null | undefined,
	currencyText: string | null | undefined = "CHF"
): string => {
	if (value === undefined || value === null) return "-";
	return formatCurrency(value, currencyText);
};

export const SUPABASE_PAGE_SIZE = 1000;

export async function fetchAllPages<T>(query: any): Promise<T[]> {
	const results: T[] = [];
	let offset = 0;
	let hasMore = true;
	while (hasMore) {
		const { data, error } = await query.range(
			offset,
			offset + SUPABASE_PAGE_SIZE - 1
		);
		if (error || !data) break;
		results.push(...(data as T[]));
		hasMore = data.length === SUPABASE_PAGE_SIZE;
		offset += SUPABASE_PAGE_SIZE;
	}
	return results;
}
